// The marketing site's cookie banner: the markup is in
// marketing/partials/footer.html, the rules for the cookie are in
// ./consent.js, and this wires the one to the other. The app's twin is
// src/components/ConsentBanner.jsx, with the same words.
//
// ── Behaviour ─────────────────────────────────────────────────────────────
//
// A notice, measurement being opt-out (#755, ./consent.js says why): with no
// current answer (never asked, a malformed or older-format cookie, or one
// past its twelve months) the measurement it describes is already running,
// and the banner says so and offers "OK", which records the answer and keeps
// it on, and "Decline", which turns it off. Two buttons of the same weight,
// and nothing else closes it on first showing: no ×, no "later". A browser
// sending Global Privacy Control with no answer on record is not shown it —
// nothing it describes is running there. The footer's "Cookie settings"
// reopens it with what is in force stated; Escape closes it again only then.
// When what is in force is off, its first button reads "Allow" rather than
// "OK", since pressing it turns the measurement on rather than keeping it.
//
// Not a dialog, and it does not trap or take focus when it appears by itself:
// focus thrown at a banner on load strands a screen-reader user who came for
// the content. It is a labelled region moved to just after the
// skip link, so it is the second stop for a keyboard and findable by landmark.
// Hiding it leaves the browser's own focus starting point where the banner was,
// so the next Tab goes on to the header. When the footer link opened it, focus
// goes into it and comes back to the link afterwards.

import {
  configureConsent, getConsent, getConsentAnswer, needsConsentNotice, privacySignalOn, renewConsent, setConsent,
} from './consent.js';

// The app's banner (src/components/ConsentBanner.jsx) says the same.
export const CONSENT_STATUS = Object.freeze({
  granted: 'Analytics and ad measurement are on.',
  denied: 'You declined analytics and ad measurement.',
  signal: 'Your browser sends Global Privacy Control, so analytics and ad measurement are off.',
});

/** The status line for what is in force, and why. */
export function consentStatus() {
  if (getConsentAnswer() === null && privacySignalOn()) return CONSENT_STATUS.signal;
  return CONSENT_STATUS[getConsent()];
}

/** The keep-or-allow button's label: "OK" while measurement is on, "Allow" while it is off. */
export const allowLabel = () => (getConsent() === 'granted' ? 'OK' : 'Allow');

export function mountConsentBanner(doc = document) {
  const banner = doc.querySelector('[data-consent]');
  if (!banner) return;

  configureConsent({ domain: banner.getAttribute('data-consent-domain') });
  renewConsent();

  const status = banner.querySelector('[data-consent-status]');
  const allow = banner.querySelector('[data-consent-choice="granted"]');
  const opener = doc.querySelector('[data-consent-open]');
  const skip = doc.querySelector('.skip-link');
  if (skip) skip.after(banner);
  else doc.body.prepend(banner);

  let returnTo = null;

  const show = (from) => {
    // The status only when "Cookie settings" asked: the notice on its own
    // already says what is on.
    status.textContent = from ? consentStatus() : '';
    status.hidden = !from;
    if (allow) allow.textContent = allowLabel();
    banner.hidden = false;
    returnTo = from;
    if (from) banner.focus();
  };

  const hide = () => {
    const back = banner.contains(doc.activeElement) ? returnTo : null;
    banner.hidden = true;
    returnTo = null;
    if (back) back.focus();
  };

  banner.addEventListener('click', (e) => {
    const choice = e.target.closest('[data-consent-choice]');
    if (!choice) return;
    setConsent(choice.getAttribute('data-consent-choice'));
    hide();
  });

  banner.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !needsConsentNotice()) hide();
  });

  if (opener) {
    opener.hidden = false;
    opener.addEventListener('click', () => show(opener));
  }

  if (needsConsentNotice()) show(null);
}
